import React, { useMemo, useState } from 'react';
import { Button, Text, View } from "../../../src/Components/Themed"
import type { BestAvailableOrderProps, BEEventProps, EventOrderStatProps, EventProps, LeagueProps, MarketProps, MatchProps, OrderProps, TournamentProps, TradeProps } from '../../../src/types';
import { Components } from 'be-components';
import moment from 'moment-mini';
import { useColors } from '../../../src/constants/useColors';
import { Image } from 'react-native';
import { Icons } from '../../../src/Components';
import { MarketComponentHelpers } from '../../../src/MarketComponents/api';
import LeagueSelector from './LeagueSelector';
import SearchBox from '../../../src/Components/SearchBox';
import HelpModal from './HelpModal';
import EventCarousel from './EventCarousel';
import FeaturedEventCard from './FeaturedEventCard';
import AdServer from '../../../src/AdServer';

type MarketFilterProps = {
    events:EventProps[],
    layout: 'banner' | 'card',
    active_league:string,
    mode:'desktop'|'mobile',
    insets:{ top:number, bottom:number, left:number, right:number },
    tournaments:TournamentProps[],
    leagues:LeagueProps[],
    markets:MarketProps[]
    latest_trades:{ [key:string]:TradeProps[] },
    best_available_orders:{ [key:string]:BestAvailableOrderProps[] },
    matches:MatchProps[],
    event_order_stats: { [key:string]:EventOrderStatProps[] },
    authenticated?:boolean,
    show_ads?:boolean,
    device_width?:number,
    onChangeLeague: (league:string) => void,
    onSelectContest: (contest_type:'team'|'tournament'|'match', contest_id:string, league_id:string) => void,
    onBuyOrder?: (order:OrderProps) => void,
    onClaimBonus?: () => void,
    onAdView?: (event:BEEventProps) => void,
    onAdClick?: (event:BEEventProps) => void
}

const default_markets = [
    { league_id: '3', market_id: '2' },
    { league_id: '1', market_id: '1' },
    { league_id: '2', market_id: '1' }
]

const MarketFilterCarousel = ({ events, leagues, layout, latest_trades, best_available_orders, markets, mode, insets, tournaments, matches, active_league, event_order_stats, authenticated, show_ads, device_width, onSelectContest, onChangeLeague, onBuyOrder, onClaimBonus, onAdView, onAdClick }:MarketFilterProps) => {
    const [ league_selector, setLeagueSelector ] = useState<{
        visible:boolean,
    }>({
        visible: false,
    })
    const [ search_query, setSearchQuery ] = useState('');
    const [ search_visible, setSearchVisible ] = useState(false);
    const [ help_visible, setHelpVisible ] = useState(false);
    const Colors = useColors();
    const sumByProperty = (arr:any, prop:string) => arr.reduce((acc:any, obj:any) => acc + parseInt(obj[prop]), 0);

    const sorted_keys:string[] = useMemo(() => {
        let keys = Object.keys(event_order_stats);

        // Filter by search query if present
        if(search_query.trim() !== '') {
            const query = search_query.toLowerCase();
            keys = keys.filter((key) => {
                const parsed_key = key.split(':');
                const contest_type = parsed_key[0];
                const contest_id = parsed_key[1];

                if(contest_type === 'team') {
                    const event = events.find(e => e.event_id == contest_id);
                    if(!event){ return false }

                    const searchableFields = [
                        event.away?.market_name?.toLowerCase() || '',
                        event.away?.name?.toLowerCase() || '',
                        event.home?.market_name?.toLowerCase() || '',
                        event.home?.name?.toLowerCase() || ''
                    ];

                    return searchableFields.some(field => field.includes(query));
                }
                return false;
            });
        }

        return keys.sort((keyA, keyB) => {
            let parsed_key_a = keyA.split(':');
            let parsed_key_b = keyB.split(':');
            let priority_a = parsed_key_a[0] == 'team' ? events.find(e => e.event_id == parsed_key_a[1])?.promoted ?? 0 : 0
            let priority_b = parsed_key_b[0] == 'team' ? events.find(e => e.event_id == parsed_key_b[1])?.promoted ?? 0 : 0
            const sumA = sumByProperty(event_order_stats[keyA], 'open_order_amount') + (priority_a * 99999)
            const sumB = sumByProperty(event_order_stats[keyB], 'open_order_amount') + (priority_b * 99999)
            return sumB - sumA; // Sort in descending order
        });
    },[active_league, search_query, JSON.stringify(events), JSON.stringify(event_order_stats)]).slice(0, 25);

    // Future: Inject ads into the data array for card layout
    // For now, just return sorted_keys without ads
    const sorted_keys_with_ads:string[] = useMemo(() => {
        return sorted_keys;
    }, [sorted_keys]);

    const renderContests = (data:{ item:string, index?:number, horizontal?:boolean }) => {
        const contest_type = data.item.split(':')[0]
        const contest_id = data.item.split(':')[1]
        if(!contest_type || !contest_id){ return <></> }
        let contest_trades = latest_trades[`${contest_type}:${contest_id}`] ?? []
        switch(contest_type){
            case 'team':
                const event = events.find(e => e.event_id == contest_id)
                if(!event){ return <></> }
                const league = leagues.find(l => l.league_id == event.league_id)
                const default_market = default_markets.find(mk => mk.league_id == event.league_id)?.market_id ?? '2'
                const market = markets.find(mk => mk.market_id == default_market);
                if(!market){ return <></> }
                let away_trade = contest_trades.find(t => t.market_type == 'FOR_MONEY' && t.market_id == market.market_id && t.side != market.trade_side);
                let home_trade = contest_trades.find(t => t.market_type == 'FOR_MONEY' && t.market_id == market.market_id && t.side == market.trade_side);
                return (
                    <Button transparent key={contest_id.toString()} style={{ minWidth:150, padding:10, borderRadius:0, borderLeftWidth:data.horizontal?1:0, borderBottomWidth:data.horizontal?0:1, borderColor:Colors.borders.light }} onPress={() => onSelectContest(contest_type, contest_id, event.league_id)}>
                        <View transparent nativeID='time_detail' style={{ flexDirection:'row', alignItems:'center', padding:2 }}>
                            {league ?
                            <Text theme='light' size={10} style={{ flex:1 }}>{league.league_name}</Text>
                            :<></>}
                            <Text theme='light' size={10} textAlign='center'>{['created','scheduled'].includes(event.time_detail ?? '') ? moment(event.scheduled_datetime).format('ddd hh:mm a') : event.time_detail}</Text>
                        </View>
                        <View transparent nativeID='away' style={{ padding:3, flexDirection:'row', alignItems:'center' }}>
                            <Image
                                source={{ uri: event.away?.image?.url }}
                                style={{ height:data.horizontal?16:24, width:data.horizontal?16:24 }}
                                resizeMode='contain'
                            />
                            <View transparent style={{ flex:1, marginLeft:5 }}>
                                <Text theme='h2' size={12}>{data.horizontal ? event.away?.abbr : `${event.away?.market_name} ${event.away?.name}`}</Text>
                                {!data.horizontal && event.away_sub_title ?
                                <Text theme='light' style={{marginTop:3}}>{event.away_sub_title}</Text>
                                :<></>}
                            </View>
                            {away_trade ?
                            <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                                {market.var_1_required ?
                                <Text theme='h1' size={12} style={{ marginRight:5 }}>{MarketComponentHelpers.getVar1Label(market, away_trade.var_1)}</Text>
                                :<></>}
                                <Text theme='h2' size={12}>{MarketComponentHelpers.getOddsLabel(away_trade.odds)}</Text>
                            </View>
                            :<></>}
                        </View>
                        <View transparent nativeID='home' style={{ padding:3, flexDirection:'row', alignItems:'center' }}>
                            <Image
                                source={{ uri: event.home?.image?.url }}
                                style={{ height:data.horizontal?16:24, width:data.horizontal?16:24 }}
                                resizeMode='contain'
                            />
                            <View transparent style={{ flex:1, marginLeft:5 }}>
                                <Text theme='h2' size={12}>{data.horizontal ? event.home?.abbr : `${event.home?.market_name} ${event.home?.name}`}</Text>
                                {!data.horizontal && event.home_sub_title ?
                                <Text theme='light' style={{marginTop:3}}>{event.home_sub_title}</Text>
                                :<></>}
                            </View>
                            {home_trade ?
                            <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                                {market.var_1_required ?
                                <Text theme='h1' size={12} style={{ marginRight:5 }}>{MarketComponentHelpers.getVar1Label(market, home_trade.var_1)}</Text>
                                :<></>}
                                <Text theme='h2' size={12}>{MarketComponentHelpers.getOddsLabel(home_trade.odds)}</Text>
                            </View>
                            :<></>}
                        </View>
                    </Button>
                )
            default: return <></>
        }
    }

    const renderLoadingState = () => {
        return (
            <View style={{ flex: 1 }}>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                    <Icons.BettorEdgeIcon size={48} color={Colors.text.h1} />
                    <Text theme='h1' style={{ marginTop: 24, textAlign: 'center' }}>Loading Events...</Text>
                    <Text theme='description' style={{ marginTop: 10, textAlign: 'center' }}>
                        Fetching the latest markets from BettorEdge
                    </Text>
                </View>
            </View>
        );
    };

    const renderEmptyState = () => {
        // If there's a search query, show search-specific empty state
        if (search_query.trim() !== '') {
            return (
                <View style={{ flex: 1 }}>
                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                        <Icons.SearchIcon size={48} color={Colors.text.description} />
                        <Text theme='h1' style={{ marginTop: 20, textAlign: 'center' }}>No events found</Text>
                        <Text theme='description' style={{ marginTop: 10, textAlign: 'center' }}>
                            Try adjusting your search or select a different league
                        </Text>
                        <Button
                            style={{ marginTop: 20 }}
                            onPress={() => {
                                setSearchQuery('');
                                setSearchVisible(false);
                            }}
                        >
                            <Text theme='h2'>Clear Search</Text>
                        </Button>
                    </View>
                </View>
            );
        }

        // Otherwise show generic empty state
        return (
            <View style={{ flex: 1 }}>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                    <Icons.BettorEdgeIcon size={48} color={Colors.text.description} />
                    <Text theme='h1' style={{ marginTop: 24, textAlign: 'center' }}>No Events Available</Text>
                    <Text theme='description' style={{ marginTop: 10, textAlign: 'center' }}>
                        There are currently no events for this league
                    </Text>
                </View>
            </View>
        );
    };

    const renderFeaturedCard = (data: { item: string, index: number }) => {
        const parts = data.item.split(':');
        const contest_type = parts[0];
        const contest_id = parts[1];

        if (contest_type !== 'team' || !contest_id) {
            return <></>;
        }

        const event = events.find(e => e.event_id == contest_id);
        if (!event) {
            return <></>;
        }

        const league = leagues.find(l => l.league_id == event.league_id);
        const default_market = default_markets.find(mk => mk.league_id == event.league_id)?.market_id ?? '2';
        const market = markets.find(mk => mk.market_id == default_market);
        const contest_trades = latest_trades[`${contest_type}:${contest_id}`] ?? [];
        const contest_available_orders = best_available_orders[`${contest_type}:${contest_id}`] ?? [];

        return (
            <FeaturedEventCard
                event={event}
                league={league}
                market={market}
                trades={contest_trades}
                available_orders={contest_available_orders}
                onPress={() => onSelectContest('team', contest_id, event.league_id)}
                onBuyOrder={onBuyOrder}
                showBonusBanner={!authenticated}
                onClaimBonus={onClaimBonus}
            />
        );
    };

    return (
        <View transparent style={{ flex:1 }}>
            {layout == 'banner' ?
            <View transparent style={{ flexDirection: 'column' }}>
                <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:8, paddingVertical:8 }}>
                    <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                        <View transparent style={{ marginRight:8 }}>
                            <Icons.BettorEdgeIcon size={16} color={Colors.text.h1} />
                        </View>
                        <View transparent style={{ flexDirection:'column' }}>
                            <Text theme='h1'>{active_league} Markets</Text>
                            <Text theme='description' style={{ marginTop:2 }}>Lines powered by BettorEdge</Text>
                        </View>
                    </View>
                    <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                        <Button
                            float
                            style={{ flexDirection:'row', alignItems:'center', padding:8, marginRight:3 }}
                            onPress={() => setHelpVisible(true)}
                        >
                            <Icons.QuestionIcon size={12} color={Colors.text.h1} />
                        </Button>
                        <Button
                            float
                            style={{ flexDirection:'row', alignItems:'center', padding:8, marginRight:3 }}
                            onPress={() => {
                                if(search_visible) {
                                    setSearchQuery('');
                                    setSearchVisible(false);
                                } else {
                                    setSearchVisible(true);
                                }
                            }}
                        >
                            {search_visible ?
                                <Icons.CloseIcon size={10} color={Colors.text.h1} />
                                :
                                <Icons.SearchIcon size={10} color={Colors.text.h1} />
                            }
                        </Button>
                        <Button float style={{ flexDirection:'row', alignItems:'center', padding:8 }} onPress={() => setLeagueSelector({ ...league_selector, visible: true })}>
                            <Text theme='description' size={10} style={{ marginRight:3 }}>{active_league}</Text>
                            <Icons.ChevronIcon direction='down' size={6} color={Colors.text.h1} />
                        </Button>
                    </View>
                </View>
                {search_visible ?
                <View style={{ paddingHorizontal:10, paddingBottom:5 }}>
                    <SearchBox
                        placeholder="Search teams..."
                        hide_search_button={true}
                        onChange={(text) => setSearchQuery(text)}
                    />
                </View>
                :<></>}
                <View style={{ flexDirection:'row' }}>
                    <Components.AutoScrollingFlatList
                        data={sorted_keys}
                        speed={10}
                        horizontal
                        renderItem={({ item, index }) => renderContests({ item:item as string, index, horizontal:true })}
                        key='popular_list'
                    />
                </View>
            </View>
            :layout == 'card'?
            <View transparent style={{ flex: 1 }}>
                <View>
                    <View type='header' style={{ padding:10, flexDirection:'row', justifyContent:'space-between', alignItems:'center' }}>
                        <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ marginRight:8 }}>
                                <Icons.BettorEdgeIcon size={16} color={Colors.text.h1} />
                            </View>
                            <View transparent style={{ flexDirection:'column' }}>
                                <Text theme='h1' color={Colors.text.h1}>{active_league} Markets</Text>
                                <Text theme='description' style={{ marginTop:2 }}>Lines powered by BettorEdge</Text>
                            </View>
                        </View>
                        <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                            <Button
                                float
                                style={{ flexDirection:'row', alignItems:'center', padding:8, marginRight:3 }}
                                onPress={() => setHelpVisible(true)}
                            >
                                <Icons.QuestionIcon size={12} color={Colors.text.h1} />
                            </Button>
                            <Button
                                float
                                style={{ flexDirection:'row', alignItems:'center', padding:8, marginRight:3 }}
                                onPress={() => {
                                    if(search_visible) {
                                        setSearchQuery('');
                                        setSearchVisible(false);
                                    } else {
                                        setSearchVisible(true);
                                    }
                                }}
                            >
                                {search_visible ?
                                    <Icons.CloseIcon size={10} color={Colors.text.h1} />
                                    :
                                    <Icons.SearchIcon size={10} color={Colors.text.h1} />
                                }
                            </Button>
                            <Button float style={{ flexDirection:'row', alignItems:'center', padding:8 }} onPress={() => setLeagueSelector({ ...league_selector, visible: true })}>
                                <Text theme='description' size={10} style={{ marginRight:3 }} color={Colors.text.descriptionLight}>{active_league}</Text>
                                <Icons.ChevronIcon direction='down' size={6} color={Colors.text.h1} />
                            </Button>
                        </View>
                    </View>
                    {search_visible && (
                        <View transparent style={{ marginBottom: 10 }}>
                            <SearchBox
                                placeholder="Search teams..."
                                hide_search_button={true}
                                onChange={(text) => setSearchQuery(text)}
                            />
                        </View>
                    )}
                </View>
                {sorted_keys.length > 0 ? (
                    <EventCarousel
                        data={sorted_keys_with_ads}
                        renderItem={renderFeaturedCard}
                        autoAdvanceDelay={12000}
                        pauseOnInteraction={true}
                    />
                ) : Object.keys(event_order_stats).length === 0 && events.length === 0 ? (
                    renderLoadingState()
                ) : (
                    renderEmptyState()
                )}
                {show_ads ?
                <AdServer
                    placement="events_feed"
                    ad_type={'banner'}
                    device_width={device_width ?? 300}
                    onViewAd={onAdView}
                    onClickAd={onAdClick}
                />
                :<></>}
            </View>
            :<></>}
            <LeagueSelector
                leagues={leagues}
                visible={league_selector.visible}
                active_league={active_league}
                mode={mode}
                insets={insets}
                onClose={() => setLeagueSelector({ ...league_selector, visible: false })}
                onLeagueSelect={(league) => {
                    setLeagueSelector({ ...league_selector, visible: false })
                    onChangeLeague(league)
                }}
            />
            <HelpModal
                visible={help_visible}
                mode={mode}
                insets={insets}
                onClose={() => setHelpVisible(false)}
            />
        </View>
    )
}

export default MarketFilterCarousel
